import { useTranslation } from 'react-i18next'
import { Plus, Edit, Trash2, Search, HelpCircle } from 'lucide-react'
import type { QuestionTemplate } from '../../types/case-container'

export interface FilterBarProps {
  searchTerm: string
  categoryFilter: string
  categories: string[]
  onSearchChange: (value: string) => void
  onCategoryChange: (value: string) => void
  onAdd: () => void
}

export function FilterBar({
  searchTerm,
  categoryFilter,
  categories,
  onSearchChange,
  onCategoryChange,
  onAdd,
}: FilterBarProps): React.ReactElement {
  const { t } = useTranslation('settings')
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative min-w-[200px] flex-1">
        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value)
          }}
          placeholder={t('templates.searchPlaceholder')}
          className="w-full rounded-md border border-slate-300 bg-white py-1.5 pr-3 pl-9 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        />
      </div>
      <select
        value={categoryFilter}
        onChange={(e) => {
          onCategoryChange(e.target.value)
        }}
        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
      >
        <option value="all">{t('templates.allCategories')}</option>
        {categories.map((cat) => (
          <option key={cat} value={cat}>
            {cat}
          </option>
        ))}
      </select>
      <button
        onClick={onAdd}
        className="inline-flex items-center gap-1.5 rounded-md bg-fuchsia-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-fuchsia-700"
      >
        <Plus className="h-4 w-4" />
        {t('templates.addTemplate')}
      </button>
    </div>
  )
}

export interface EmptyTemplatesProps {
  hasTemplates: boolean
  onAdd: () => void
}

export function EmptyTemplates({ hasTemplates, onAdd }: EmptyTemplatesProps): React.ReactElement {
  const { t } = useTranslation('settings')
  return (
    <div className="rounded-lg border border-dashed border-slate-300 py-8 text-center dark:border-slate-700">
      <HelpCircle className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
      <p className="mt-3 text-slate-600 dark:text-slate-400">
        {hasTemplates ? t('templates.noTemplatesMatch') : t('templates.noTemplates')}
      </p>
      {!hasTemplates && (
        <button
          onClick={onAdd}
          className="mt-2 text-sm text-fuchsia-600 hover:underline dark:text-fuchsia-400"
        >
          {t('templates.createFirst')}
        </button>
      )}
    </div>
  )
}

export interface TemplateGroupListProps {
  groupedTemplates: Record<string, QuestionTemplate[]>
  deleting: boolean
  onEdit: (template: QuestionTemplate) => void
  onDelete: (id: string) => Promise<void>
}

export function TemplateGroupList({
  groupedTemplates,
  deleting,
  onEdit,
  onDelete,
}: TemplateGroupListProps): React.ReactElement {
  const { t: tCommon } = useTranslation('common')
  return (
    <div className="space-y-4">
      {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
        <div key={category}>
          <h4 className="mb-2 text-sm font-medium text-slate-500 dark:text-slate-400">
            {category} ({categoryTemplates.length})
          </h4>
          <div className="space-y-2">
            {categoryTemplates.map((template) => (
              <div
                key={template.id}
                className="flex items-start justify-between rounded-lg border border-slate-200 p-3 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/50"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {template.description ?? template.questionText}
                  </p>
                  <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">
                    {template.questionText}
                  </p>
                </div>
                <div className="ml-3 flex items-center gap-1">
                  <button
                    onClick={() => {
                      onEdit(template)
                    }}
                    className="rounded p-1.5 text-slate-400 hover:text-fuchsia-600 dark:hover:text-fuchsia-400"
                    title={tCommon('actions.edit')}
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      void onDelete(template.id)
                    }}
                    disabled={deleting}
                    className="rounded p-1.5 text-slate-400 hover:text-red-600 disabled:opacity-50 dark:hover:text-red-400"
                    title={tCommon('actions.delete')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
